import type { FC } from "react"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { useMutation, useQuery } from "@apollo/client"
import * as Tabs from "@radix-ui/react-tabs"

import styles from "./CompanyEditPage.module.scss"

import CompanyBreadcrumb from "@/souhl_admin/components/features/companies/Breadcrumb/CompanyBreadcrumb"
import CompanyTabBar from "@/souhl_admin/components/features/companies/CompanyTabBar/CompanyTabBar"
import SalonSelectAndHome from "@/souhl_admin/components/features/companies/SalonSelectAndHome/SalonSelectAndHome"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"

const GetCompany = graphql(/* GraphQL */ `
	query GetCompanyForEdit($param: CompanyParam!) {
		company(param: $param) {
			company {
				id
				name
			}
		}
	}
`)

const UpdateCompany = graphql(/* GraphQL */ `
	mutation UpdateCompany($input: UpdateCompanyInput!) {
		updateCompany(input: $input) {
			company {
				id
				name
			}
		}
	}
`)

const CompanyEditPage: FC = () => {
	const navigate = useNavigate()
	const { companyId } = useParams<{ companyId: string }>()
	const [name, setName] = useState("")
	const [errors, setErrors] = useState<{ name?: string }>({})

	const { data, loading: queryLoading } = useQuery(GetCompany, {
		variables: {
			param: {
				id: companyId ?? "",
			},
		},
		skip: !companyId,
		fetchPolicy: "cache-first",
	})

	useEffect(() => {
		if (data?.company.company?.name) {
			setName(data.company.company.name)
		}
	}, [data])

	const [updateCompany, { loading: mutationLoading }] = useMutation(
		UpdateCompany,
		{
			onCompleted: (data) => {
				if (data?.updateCompany?.company?.id) {
					const companyId = data.updateCompany.company.id
					navigate(`/souhl_admin/companies/${companyId}`)
				}
			},
			onError: (error) => {
				console.error("Failed to update company:", error)
			},
		}
	)

	const loading = queryLoading || mutationLoading

	const validateForm = () => {
		const newErrors: { name?: string } = {}

		if (!name.trim()) {
			newErrors.name = "名前は必須です"
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSubmit = async () => {
		if (!validateForm() || !companyId) {
			return
		}

		await updateCompany({
			variables: {
				input: {
					id: companyId,
					name: name.trim(),
				},
			},
		})
	}

	const handleCancel = () => {
		navigate(`/souhl_admin/companies/${companyId}`)
	}

	if (queryLoading && !data) {
		return (
			<div className={styles.loadingContainer}>
				<div className={styles.loading}>読み込み中...</div>
			</div>
		)
	}

	if (!data?.company.company) {
		return (
			<div className={styles.container}>
				<div className={styles.error}>会社が見つかりませんでした</div>
			</div>
		)
	}

	const companyName = data.company.company.name

	return (
		<div className={styles.container}>
			<CompanyBreadcrumb
				companyName={companyName}
				companyId={companyId ?? ""}
				isEditPage
			/>
			<SalonSelectAndHome companyId={companyId ?? ""} />

			<Tabs.Root value="general">
				<CompanyTabBar
					activeTab="general"
					companyId={companyId ?? ""}
					isEditPage
				/>
			</Tabs.Root>

			<div className={styles.formContainer}>
				<div className={styles.formHeader}>
					<h2 className={styles.formTitle}>会社情報</h2>
					<div className={styles.actions}>
						<button
							className={styles.cancelButton}
							onClick={handleCancel}
							disabled={loading}
						>
							キャンセル
						</button>
						<button
							className={styles.updateButton}
							onClick={handleSubmit}
							disabled={loading}
						>
							{mutationLoading ? "更新中..." : "更新"}
						</button>
					</div>
				</div>

				<div className={styles.formContent}>
					<div className={styles.formField}>
						<label className={styles.label}>ID</label>
						<div className={styles.value}>{data.company.company.id}</div>
					</div>
					<div className={styles.formField}>
						<label className={styles.label}>名前</label>
						<div className={styles.inputWrapper}>
							<input
								type="text"
								className={`${styles.input} ${errors.name ? styles.error : ""}`}
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="株式会社SOUHL"
								disabled={loading}
							/>
							{errors.name && (
								<span className={styles.errorMessage}>{errors.name}</span>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default CompanyEditPage
